import { getUserDetails, makeGetCall, makePostCall } from './helper';

/**
 * Where the reporting editor and the DICOM viewer land on a dual-monitor desk.
 *
 * 'viewer-other'    (default — what everyone got before this preference existed):
 *                   reporting opens as a TAB in the current browser window, so it stays
 *                   on the monitor the radiologist is already looking at, and the viewer
 *                   pops up on the other monitor inset to 95% of that screen.
 *
 * 'reporting-other' the mirror image, for radiologists who would rather keep images in
 *                   front of them: reporting opens as its own WINDOW on the other
 *                   monitor, and the viewer fills the monitor they are sitting at.
 *
 * The viewer's target screen needs no special-casing between the two modes. The viewer is
 * launched from inside the reporting page, and MonitorManager always places it on the
 * screen its opener is NOT on — so moving reporting to the other monitor sends the viewer
 * back to the original one for free. Only the viewer's sizing and how /oo-report itself is
 * opened actually differ.
 */
export type WorkstationLayout = 'viewer-other' | 'reporting-other';

export const DEFAULT_WORKSTATION_LAYOUT: WorkstationLayout = 'viewer-other';

/**
 * Persisted in the generic `user_preferences` table (up_username, up_type, up_value JSONB)
 * that already backs mouse actions, favourite tools, hotkeys and panel positions — so this
 * needs no migration and no new column, just a new up_type. Stored as an object rather
 * than a bare string so further workstation settings can join it later without another
 * preference type.
 */
const PREFERENCE_TYPE = 'workstation_layout';

/**
 * localStorage is ONLY a synchronous cache of the row above, never the source of truth.
 * The preference is read at click time immediately before `window.open`, where awaiting a
 * round-trip would add latency and risk the browser dropping user activation (a blocked
 * popup). Logout calls localStorage.clear(); preloadWorkstationLayout() refills it from
 * the database on the next boot, which is a full page load after every login.
 *
 * Keyed by username so that a shared workstation can never serve one radiologist's seating
 * habit to the next person who logs in.
 */
const STORAGE_KEY = 'pacs_workstation_layout';

const currentUsername = (): string => {
  try {
    const user = getUserDetails();
    return user?.username || user?.user_name || '';
  } catch {
    return '';
  }
};

const asLayout = (value: any): WorkstationLayout | null =>
  value === 'reporting-other' || value === 'viewer-other' ? value : null;

const readCache = (): Record<string, WorkstationLayout> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Corrupt JSON, or storage blocked on a locked-down browser profile.
    return {};
  }
};

const writeCache = (username: string, layout: WorkstationLayout): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readCache(), [username]: layout })
    );
  } catch (e) {
    console.warn('workstationLayout: failed to write localStorage cache', e);
  }
};

/** Synchronous read, safe to call immediately before window.open. */
export const getWorkstationLayout = (): WorkstationLayout => {
  const username = currentUsername();
  if (!username) return DEFAULT_WORKSTATION_LAYOUT;

  return asLayout(readCache()[username]) || DEFAULT_WORKSTATION_LAYOUT;
};

/**
 * Hydrate the cache from the database. Called from index.js before the app mounts, so the
 * first click already reads the stored value. Safe no-op when nobody is logged in.
 */
export const preloadWorkstationLayout = async (): Promise<WorkstationLayout | null> => {
  const username = currentUsername();
  if (!username) return null;

  try {
    const resp = await makeGetCall(
      `/get-user-preference?username=${encodeURIComponent(username)}&type=${PREFERENCE_TYPE}`
    );
    const stored = resp?.data?.data;
    const layout = asLayout(typeof stored === 'string' ? stored : stored?.layout);

    // A successful fetch is authoritative: mirror exactly what the row holds (the default
    // when there is no row yet) so a preference cleared server-side cannot linger here.
    writeCache(username, layout || DEFAULT_WORKSTATION_LAYOUT);
    return layout;
  } catch (e) {
    // Offline or the endpoint failed — keep whatever the cache already holds.
    console.warn('workstationLayout: preload failed, using cached/default layout', e);
    return null;
  }
};

/**
 * Persist the preference. Writes the cache first so the very next click honours the new
 * value even while the request is still in flight. Resolves false when the row could not
 * be saved, so the caller can tell the user it will not follow them to another machine.
 */
export const setWorkstationLayout = async (layout: WorkstationLayout): Promise<boolean> => {
  const username = currentUsername();
  if (!username) return false;

  writeCache(username, layout);

  try {
    await makePostCall('/save-user-preference', {
      username,
      type: PREFERENCE_TYPE,
      value: { layout },
    });
    return true;
  } catch (e) {
    console.error('workstationLayout: failed to persist preference', e);
    return false;
  }
};

/**
 * True when reporting should open on the other monitor and the viewer should fill the one
 * the user is sitting at. Read at click time (never memoised), so flipping the preference
 * takes effect in every already-open tab without a reload.
 */
export const isReportingOnOtherScreen = (): boolean =>
  getWorkstationLayout() === 'reporting-other';
